import { EventSourcePolyfill } from 'event-source-polyfill';
import { apiFetch, apiFetchData, withAuthHeader, type AuthRetry } from '@/shared/lib/api';
import { config } from '@/shared/config';
import { useAuthStore } from '@/features/auth/store';
import type {
  DeleteAllNotificationsResponse,
  ListNotificationsOptions,
  MarkAllReadResponse,
  NotificationListResponse,
  NotificationPreferencesResponse,
  OrderTrackingResponse,
  UpdateNotificationPreferencesRequest,
} from './types';

const OWNER_BASE = '/api/owner/restaurants';
const PUBLIC_BASE = '/api/public';

function getAuthRetry(): AuthRetry {
  return {
    getToken: () => useAuthStore.getState().token,
    refreshTokens: () => useAuthStore.getState().refreshTokens(),
  };
}

function getToken(): string {
  const t = useAuthStore.getState().token;
  if (!t) throw new Error('No token');
  return t;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return '';
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return `?${qs}`;
}

/**
 * Owner-notifications + public-tracking REST client.
 *
 * SSE stream: JWT rides in the `Authorization: Bearer` header via
 * `event-source-polyfill` (the native `EventSource` can't set custom
 * headers). Keeping the token out of the URL avoids leaks into server
 * access logs, browser history, and Referer headers.
 */
export const notificationsApi = {
  async listNotifications(
    restaurantId: string,
    opts: ListNotificationsOptions = {}
  ): Promise<NotificationListResponse> {
    const token = getToken();
    const qs = buildQuery({
      unreadOnly: opts.unreadOnly,
      limit: opts.limit,
      cursor: opts.cursor,
    });
    return apiFetchData<NotificationListResponse>(
      `${OWNER_BASE}/${restaurantId}/notifications${qs}`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async markRead(restaurantId: string, notificationId: string): Promise<void> {
    const token = getToken();
    await apiFetch<void>(
      `${OWNER_BASE}/${restaurantId}/notifications/${notificationId}/read`,
      { method: 'POST', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async markAllRead(restaurantId: string): Promise<MarkAllReadResponse> {
    const token = getToken();
    return apiFetchData<MarkAllReadResponse>(
      `${OWNER_BASE}/${restaurantId}/notifications/read-all`,
      { method: 'POST', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async deleteNotification(restaurantId: string, notificationId: string): Promise<void> {
    const token = getToken();
    await apiFetch<void>(
      `${OWNER_BASE}/${restaurantId}/notifications/${notificationId}`,
      { method: 'DELETE', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async deleteAllNotifications(restaurantId: string): Promise<DeleteAllNotificationsResponse> {
    const token = getToken();
    return apiFetchData<DeleteAllNotificationsResponse>(
      `${OWNER_BASE}/${restaurantId}/notifications`,
      { method: 'DELETE', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async getPreferences(restaurantId: string): Promise<NotificationPreferencesResponse> {
    const token = getToken();
    return apiFetchData<NotificationPreferencesResponse>(
      `${OWNER_BASE}/${restaurantId}/notification-preferences`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async updatePreferences(
    restaurantId: string,
    patch: UpdateNotificationPreferencesRequest
  ): Promise<NotificationPreferencesResponse> {
    const token = getToken();
    return apiFetchData<NotificationPreferencesResponse>(
      `${OWNER_BASE}/${restaurantId}/notification-preferences`,
      { method: 'PATCH', headers: withAuthHeader(undefined, token), body: patch },
      getAuthRetry()
    );
  },

  async getOrderTracking(trackingToken: string): Promise<OrderTrackingResponse> {
    return apiFetchData<OrderTrackingResponse>(
      `${PUBLIC_BASE}/orders/track/${encodeURIComponent(trackingToken)}`,
      { method: 'GET' }
    );
  },

  openNotificationStream(restaurantId: string, jwt: string): EventSourcePolyfill {
    const base = config.apiBaseUrl ?? '';
    const url = `${base}${OWNER_BASE}/${restaurantId}/notifications/stream`;
    return new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${jwt}` },
      // Polyfill's internal retry loop is disabled — the stream hook
      // owns reconnect scheduling (exponential backoff with jitter).
      heartbeatTimeout: 45_000,
    });
  },
};

export const deviceApi = {
  register(body: { fcmToken: string; platform: string; deviceId: string }): Promise<{ deviceId: string }> {
    const token = getToken();
    return apiFetch<{ deviceId: string }>('/api/devices', {
      method: 'POST',
      headers: withAuthHeader(undefined, token),
      body,
    }, getAuthRetry());
  },

  unregister(deviceId: string): Promise<void> {
    const token = getToken();
    return apiFetch<void>(`/api/devices/${encodeURIComponent(deviceId)}`, {
      method: 'DELETE',
      headers: withAuthHeader(undefined, token),
    }, getAuthRetry());
  },
};
